import { initializeApp } from 'firebase/app';
// import { getAnalytics } from 'firebase/analytics';
import { getMessaging, getToken } from 'firebase/messaging';
import ApiCaller from '../api';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  // measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
// const analytics = getAnalytics(app);
export const messaging = getMessaging(app);

// The cache key carries the Firebase project id: a token is only valid for the
// project that issued it, so switching projects must invalidate every cached
// token rather than keep re-registering a dead one under the old key.
const FCM_CACHE_KEY = `fcmToken:${process.env.REACT_APP_FIREBASE_PROJECT_ID}`;

// Drop the unversioned key written by earlier builds (possibly a token from a
// different Firebase project, which FCM will never deliver to).
localStorage.removeItem('fcmToken');

export const readCachedFCMToken = () => localStorage.getItem(FCM_CACHE_KEY) || null;
export const clearCachedFCMToken = () => localStorage.removeItem(FCM_CACHE_KEY);

const hasSession = () => {
  const raw = localStorage.getItem('auth_token');
  return Boolean(raw && raw !== 'null' && raw !== 'undefined');
};

// True only when we may subscribe. Prompts once while the state is still `default`;
// a previous denial is never re-prompted (browsers ignore repeats, and repeated
// requests after a dismissal make Chrome auto-block the origin).
const ensurePermission = async () => {
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
};

export const generateFCMToken = async (registration) => {
  try {
    if (!(await ensurePermission())) return null;

    // Desktop build: the native layer owns the token when it has one.
    if (window?.electronAPI) {
      let electronToken;
      window.electronAPI.getFCMToken('getFCMToken', (_, fcmToken) => {
        electronToken = fcmToken;
      });
      if (electronToken) return electronToken;
    }

    // ALWAYS ask the SDK for the current token rather than returning the cached one.
    // Firebase JS v9+ dropped `onTokenRefresh`, so calling getToken() is the only way
    // to notice a rotation (site data cleared, service worker unregistered, push
    // subscription invalidated). Cheap: the SDK keeps its own IndexedDB copy and only
    // hits the network when the token actually changed. The localStorage copy is kept
    // solely so logout can unregister the right device.
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FCM_VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (!token) throw new Error('FCM token generation returned empty');

    if (window?.electronAPI) window.electronAPI.storeFCMToken(token);
    localStorage.setItem(FCM_CACHE_KEY, token);
    return token;
  } catch {
    // FCM token generation failed; ignore
    return null;
  }
};

const doRegisterDevice = async (registration) => {
  if (!hasSession()) return null;
  const token = await generateFCMToken(registration);
  if (!token) return null;

  try {
    // Posted unconditionally, even when the token is unchanged: the upsert refreshes
    // `last_seen_at`, which keeps an actively-used device out of the staleness sweep.
    await ApiCaller.Post('/api/v1/user/devices', { fcm_token: token, platform: 'WEB' });
    return token;
  } catch {
    // Registration is best-effort — never block the app on it.
    return null;
  }
};

let registerInFlight = null;

/**
 * Registers this browser/device with the backend so it receives push. Safe to call
 * on every boot and after every token rotation — the backend upserts on the token,
 * and each device gets its own row, so signing in elsewhere no longer silences this
 * one. Skipped when there is no session: an unauthenticated call would 401 and the
 * axios interceptor would hard-logout.
 *
 * Single-flight: boot (service-worker ready), the App mount, and login all call this
 * within the same tick — they collapse into one permission check + token fetch.
 */
export const registerDeviceForPush = (registration) => {
  if (!registerInFlight) {
    registerInFlight = doRegisterDevice(registration).finally(() => {
      registerInFlight = null;
    });
  }
  return registerInFlight;
};

/**
 * Removes this device from the backend registry. Must run BEFORE the session is
 * cleared, since the endpoint is authenticated. Other devices keep their push.
 */
export const unregisterDeviceForPush = async () => {
  const token = readCachedFCMToken();
  if (token && hasSession()) {
    try {
      await ApiCaller.Delete('/api/v1/user/devices', { data: { fcm_token: token } });
    } catch {
      // Best-effort: the server also prunes tokens FCM reports as dead.
    }
  }
  clearCachedFCMToken();
};
